import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Dialog,
  Chip,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { XIcon, ShareFatIcon, PencilSimpleIcon, DotsThreeIcon, FlagIcon } from '@phosphor-icons/react';
import type { User } from 'firebase/auth';
import { glassStyles } from '../../tokens/designTokens';
import { font } from '../../utils/fontHelper';
import { relativeTime, WATCHLIST_CREW_UID } from '../../utils/crewFeedUtils';
import { formatWatchedDate } from '../../utils/dateUtils';
import CommentsList from '../widgets/CommentsList';
import CommentComposer from '../widgets/CommentComposer';
import CrewPostLinkSections from '../widgets/CrewPostLinkSections';
import InitialsAvatar from '../widgets/InitialsAvatar';
import CompactMovieCard from '../widgets/CompactMovieCard';
import PostActionBar from '../widgets/PostActionBar';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';
import { useSuppressKeyboardDismissButton } from '../../contexts/NativeKeyboardContext';
import type { SocialPostData, SocialPostReviewPreview, BundleItem } from '../../utils/socialPostBuilders';
import type { CommentParent } from '@watchlist/shared';
import { unifiedGetDoc } from '@watchlist/shared';
import { useCrewActivityMovieDisplay } from '../../hooks/useCrewActivityMovieDisplay';
import WatchlistWidget from '../widgets/WatchlistWidget';

interface SocialPostDialogProps {
  open: boolean;
  onClose: () => void;
  post: SocialPostData | null;
  currentUser?: User | null;

  /** Author display info — caller resolves from user-profile cache. */
  authorName?: string;
  authorAvatar?: string;

  onLikeToggle?: () => void;
  onLikedByClick?: () => void;

  onUserClick?: (uid: string) => void;
  onMovieSelect?: (movieId: string) => void;
  onWatchlistSelect?: (watchlistId: string) => void;
  onReviewSelect?: (reviewId: string) => void;
  onPersonSelect?: (personId: string) => void;

  /** Review-post specific extras (passed by MovieDetail / UserProfile / etc.). */
  onShareReview?: (reviewId: string) => void;
  onEditReview?: () => void;
  /** When provided (and post is a review by another user), a ⋮ menu with "Report review" appears in the header. */
  onReportReview?: (reviewId: string) => void;
  isUserReview?: boolean;

  /** When set, the matching comment in the comments list is auto-paged in, scrolled
   *  into view, and briefly flashed. Driven by `?highlightCommentId=…` deep-links from
   *  `comment_liked` push notifications. */
  highlightCommentId?: string | null;

  /** Override the Dialog z-index (default MUI 1300). Set higher when rendering inside
   *  a fixed overlay such as StoryViewer (zIndex 2000). */
  zIndex?: number;

  /**
   * When true, the BottomSheet will not push/pop a browser history entry.
   * Use this when the caller handles navigation on movie/watchlist select —
   * otherwise the BottomSheet's history.go(-1) cleanup fires after navigate()
   * and pops the user back to the previous route.
   */
  disableWebHistoryBack?: boolean;
}

const CREW_DISPLAY_NAME = 'Watchlist Crew';

const _watchlistCache = new Map<string, any>();

const LiveWatchlistWidget: React.FC<{
  watchlistId: string;
  fallbackName: string;
  actorId: string;
  currentUser?: User | null;
  onClick?: () => void;
}> = ({ watchlistId, fallbackName, actorId, currentUser, onClick }) => {
  const [liveData, setLiveData] = useState<any>(_watchlistCache.get(watchlistId) ?? null);

  useEffect(() => {
    if (_watchlistCache.has(watchlistId)) return;
    unifiedGetDoc('watchlists', watchlistId)
      .then(doc => {
        if (doc?.exists?.()) {
          const data = doc.data();
          _watchlistCache.set(watchlistId, data);
          setLiveData(data);
        }
      })
      .catch(() => {});
  }, [watchlistId]);

  const moviePosters: string[] = liveData?.movieDetails
    ? liveData.movieDetails.slice(0, 2).map((m: any) => m.poster || m.Poster || m.poster_path).filter(Boolean)
    : [];

  const externalData = liveData
    ? {
        name: liveData.name || fallbackName,
        description: liveData.description,
        isPrivate: false,
        createdBy: actorId,
        createdByName: '',
        movieCount: liveData.movieCount ?? liveData.movieDetails?.length ?? 0,
        movieDetails: liveData.movieDetails || [],
        followerCount: liveData.followerCount || 0,
        collaborators: liveData.collaborators || [],
        collaboratorProfiles: liveData.collaboratorProfiles || [],
      }
    : { name: fallbackName, description: undefined, isPrivate: false, createdBy: actorId, createdByName: '' };

  return (
    <WatchlistWidget
      watchlistType={watchlistId}
      user={currentUser}
      targetUserId={actorId}
      isOtherUserWatchlist
      moviePosters={moviePosters}
      onClick={onClick}
      externalWatchlistData={externalData}
    />
  );
};

// Small components so they can call hooks (hooks can't be called inside renderPreview)
const ReviewMoviePreview: React.FC<{
  r: SocialPostReviewPreview;
  onClose: () => void;
  onMovieSelect?: (movieId: string) => void;
}> = ({ r, onClose, onMovieSelect }) => {
  const { poster, title, year, genre } = useCrewActivityMovieDisplay(
    r.movieId,
    r.moviePoster,
    r.movieTitle,
    r.movieYear,
    r.movieGenres?.[0]
  );
  return (
    <CompactMovieCard
      dense
      poster={poster}
      title={title}
      year={year || r.movieYear}
      genre={genre || r.movieGenres?.join(' • ')}
      rating={r.rating}
      onClick={onMovieSelect ? () => { onClose(); onMovieSelect(r.movieId); } : undefined}
    />
  );
};

const BundleMovieCardItem: React.FC<{
  it: BundleItem;
  onClose: () => void;
  onMovieSelect?: (movieId: string) => void;
}> = ({ it, onClose, onMovieSelect }) => {
  const { poster, title, year, genre } = useCrewActivityMovieDisplay(
    it.movieId,
    it.poster,
    it.title,
    it.year,
    it.genre
  );
  const watchedLabel = it.watchedDate
    ? formatWatchedDate(it.watchedDate, it.watchedDatePrecision, '')
    : '';
  return (
    <Box>
      <CompactMovieCard
        dense
        poster={poster}
        title={title}
        year={year}
        genre={genre}
        rating={it.rating}
        onClick={onMovieSelect ? () => { onClose(); onMovieSelect(it.movieId); } : undefined}
      />
      {watchedLabel ? (
        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: font(), mt: 0.5, ml: 0.5 }}>
          {`Watched ${watchedLabel}`}
        </Typography>
      ) : null}
    </Box>
  );
};

function commentParentFor(post: SocialPostData): CommentParent {
  switch (post.kind) {
    case 'review':     return { type: 'review',    id: post.id };
    case 'customPost': return { type: 'crewPost',  id: post.id };
    case 'watchlist':  return { type: 'watchlist', id: post.id };
    case 'bundle':     return { type: 'bundle',    id: post.id };
  }
}

const SocialPostDialog: React.FC<SocialPostDialogProps> = ({
  open,
  onClose,
  post,
  currentUser,
  authorName,
  authorAvatar,
  onLikeToggle,
  onLikedByClick,
  onUserClick,
  onMovieSelect,
  onWatchlistSelect,
  onReviewSelect,
  onPersonSelect,
  onShareReview,
  onEditReview,
  onReportReview,
  isUserReview,
  highlightCommentId,
  zIndex,
  disableWebHistoryBack,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  // user.displayName / user.photoURL come from the Firebase auth session and
  // don't refresh when the user edits their profile — pull the fresh values
  // from the profile cache so new comments don't render with stale identity.
  const { getUserDisplayName, getUserAvatar } = useUserProfileCache();
  const composerName = user ? getUserDisplayName(user.uid, user.displayName || undefined) : 'You';
  const composerAvatar = user ? getUserAvatar(user.uid) ?? user.photoURL ?? null : null;
  const {
    isKeyboardOpen,
    keyboardHeight,
    scrollContainerRef,
    scrollIntoViewOnFocus,
  } = useKeyboardAware();

  // The comment composer's Send icon sits where the global floating keyboard-dismiss button
  // renders (bottom-right, just above the keyboard). Hide that button only while this
  // dialog is open — it stays available everywhere else.
  useSuppressKeyboardDismissButton(open);

  // Live count from the CommentsList subscription. Used to drive the count next to the
  // comment icon in the action bar so it stays accurate even before the Cloud Function
  // commentCount increment has propagated back to the post doc.
  const commentsRef = React.useRef<HTMLDivElement>(null);
  const [liveCommentCount, setLiveCommentCount] = useState<number | null>(null);
  // Bumped after a new comment is submitted so CommentsList immediately re-fetches —
  // matters on native (Capacitor) where the subscription is 15 s polling, not snapshot.
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const [reportMenuAnchor, setReportMenuAnchor] = useState<HTMLElement | null>(null);
  const effectiveZ = zIndex ?? 1300;
  useEffect(() => {
    if (!open) {
      setLiveCommentCount(null);
      setCommentsRefreshKey(0);
    }
  }, [open]);

  if (!post) return null;

  const isLiked = Boolean(currentUser && post.likedBy.includes(currentUser.uid));
  const timeLabel = post.createdAt ? relativeTime(post.createdAt) : '';

  // Resolve the display name: customPosts show "Watchlist Crew" regardless of authorUid.
  const displayName =
    post.kind === 'customPost'
      ? CREW_DISPLAY_NAME
      : (authorName || 'Someone');

  // Header subtitle line (e.g. "rated 3 films" / customPost subtitle / "created a new watchlist")
  const headlineText = (() => {
    if (post.kind === 'customPost') return post.customPost?.subtitle;
    if (post.kind === 'bundle')     return post.bundle?.headerText;
    if (post.kind === 'watchlist')  return 'created a new watchlist';
    if (post.kind === 'review')     return 'wrote a review';
    return undefined;
  })();

  const renderPreview = () => {
    if (post.kind === 'review' && post.review) {
      const r = post.review;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <ReviewMoviePreview r={r} onClose={onClose} onMovieSelect={onMovieSelect} />
          <Box>
            {r.reviewText?.trim().length > 0
              ? (() => {
                  const parts = r.reviewText.trim().split(/\n{2,}/);
                  return parts.map((para, i) => (
                    <Typography key={i} sx={{
                      fontSize: 15, lineHeight: 1.5, color: theme.palette.text.primary,
                      fontFamily: font(), whiteSpace: 'pre-line',
                      mb: i < parts.length - 1 ? 1 : 0,
                    }}>
                      {para}
                    </Typography>
                  ));
                })()
              : <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: theme.palette.text.primary, fontFamily: font() }}>
                  No written review provided.
                </Typography>
            }
          </Box>
          {r.watchedDate && (
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, fontFamily: font() }}>
              {`Watched ${formatWatchedDate(r.watchedDate, r.watchedDatePrecision, '')}`}
            </Typography>
          )}
        </Box>
      );
    }

    if (post.kind === 'customPost' && post.customPost) {
      const cp = post.customPost;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {cp.isPinned && (
            <Chip
              label="Pinned"
              size="small"
              sx={{
                alignSelf: 'flex-start',
                fontFamily: font(), fontSize: 11, fontWeight: 600, height: 22,
                bgcolor: isDark ? 'rgba(255, 193, 7, 0.18)' : 'rgba(255, 193, 7, 0.25)',
                color: isDark ? '#FFD60A' : '#B45309',
              }}
            />
          )}
          <CrewPostLinkSections
            links={cp.links}
            crewActorId={WATCHLIST_CREW_UID}
            currentUser={currentUser}
            onWatchlistSelect={(id) => { onClose(); onWatchlistSelect?.(id); }}
            onMovieSelect={(id) => { onClose(); onMovieSelect?.(id); }}
            onReviewSelect={(id) => { onClose(); onReviewSelect?.(id); }}
            onUserSelect={onUserClick}
            onPersonSelect={(id) => { onClose(); onPersonSelect?.(id); }}
          />
        </Box>
      );
    }

    if (post.kind === 'watchlist' && post.watchlist) {
      const w = post.watchlist;
      return (
        <LiveWatchlistWidget
          watchlistId={w.watchlistId}
          fallbackName={w.name}
          actorId={post.authorUid}
          currentUser={currentUser}
          onClick={onWatchlistSelect ? () => { onClose(); onWatchlistSelect(w.watchlistId); } : undefined}
        />
      );
    }

    if (post.kind === 'bundle' && post.bundle) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {post.bundle.items.map((it, i) => (
            <BundleMovieCardItem
              key={it.movieId || i}
              it={it}
              onClose={onClose}
              onMovieSelect={onMovieSelect}
            />
          ))}
        </Box>
      );
    }

    return null;
  };

  const content = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', maxHeight: { xs: '100%', sm: '85vh' }, overflow: 'hidden' }}>
      {/* Top-right action row (share / edit / close) — lives OUTSIDE the scroll
          container so it always stays put without sticky-positioning tricks. Content
          below scrolls underneath these buttons; no background needed because the
          scrolling content never overlaps. */}
      <BottomSheetDraggableHeader sx={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 0.5,
        px: { xs: 3, sm: 4 },
        pt: 1.5,
        pb: 0.5,
      }}>
          {post.kind === 'review' && onShareReview && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onShareReview(post.id); }}
              sx={{ color: theme.palette.text.secondary }}
              aria-label="Share review"
            >
              <ShareFatIcon />
            </IconButton>
          )}
          {post.kind === 'review' && isUserReview && onEditReview && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEditReview(); }}
              sx={{ color: theme.palette.text.secondary }}
              aria-label="Edit review"
            >
              <PencilSimpleIcon />
            </IconButton>
          )}
          {post.kind === 'review' && !isUserReview && onReportReview && (
            <>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setReportMenuAnchor(e.currentTarget); }}
                sx={{ color: theme.palette.text.secondary }}
                aria-label="More options"
              >
                <DotsThreeIcon  weight="bold" />
              </IconButton>
              <Menu
                anchorEl={reportMenuAnchor}
                open={Boolean(reportMenuAnchor)}
                onClose={() => setReportMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ zIndex: effectiveZ + 1 }}
                slotProps={{ paper: { sx: { ...glassStyles.menu(isDark), minWidth: 180, zIndex: effectiveZ + 1 } } }}
              >
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setReportMenuAnchor(null);
                    onReportReview(post.id);
                  }}
                >
                  <ListItemIcon><FlagIcon size={20} color={theme.palette.text.secondary} /></ListItemIcon>
                  <ListItemText primary="Report review" primaryTypographyProps={{ fontFamily: font(), fontSize: 14 }} />
                </MenuItem>
              </Menu>
            </>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
            <XIcon />
          </IconButton>
        </BottomSheetDraggableHeader>

      {/* Scrollable content — everything below the fixed action row scrolls inside this box. */}
      <Box
        ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
        sx={{ flex: '1 1 auto', overflowY: 'auto', px: { xs: 3, sm: 4 }, pt: 1, pb: 1 }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
          <Box
            onClick={onUserClick && post.kind !== 'customPost' ? () => { onClose(); onUserClick(post.authorUid); } : undefined}
            sx={{ cursor: onUserClick && post.kind !== 'customPost' ? 'pointer' : 'default', flexShrink: 0, mt: 0.5 }}
          >
            <InitialsAvatar src={authorAvatar} name={displayName} size={34} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="span"
              sx={{ fontFamily: font(), fontSize: 14, lineHeight: 1.4, color: theme.palette.text.primary }}
            >
              <strong>{displayName}</strong>
              {headlineText ? ` ${headlineText}` : ''}
            </Typography>
            {timeLabel ? (
              <Typography sx={{ fontFamily: font(), fontSize: 12, color: theme.palette.text.secondary, mt: -0.5 }}>
                {timeLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {/* Preview */}
        {renderPreview()}

        {/* Inline action bar (no separate footer row) */}
        {onLikeToggle && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <PostActionBar
              likeCount={post.likeCount}
              isLiked={isLiked}
              commentCount={Math.max(post.commentCount, liveCommentCount ?? 0)}
              onLikeToggle={onLikeToggle}
              onCommentClick={() => { commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              onLikedByClick={onLikedByClick}
              disabled={!currentUser}
            />
          </Box>
        )}

        {/* Top margin so the "Comments" header always has breathing room from the
            preview / action bar above — without this, watchlist posts whose caller
            doesn't pass onLikeToggle end up with the preview butting against the title. */}
        <Box ref={commentsRef} sx={{ mt: onLikeToggle ? 1 : 3 }}>
          <CommentsList
            parent={commentParentFor(post)}
            onUserClick={onUserClick}
            onCountChange={setLiveCommentCount}
            refreshKey={commentsRefreshKey}
            highlightId={highlightCommentId}
            popoverZIndex={zIndex ? zIndex + 100 : undefined}
          />
        </Box>
      </Box>

      {/* Fixed footer: composer only */}
      {user && (
        <Box sx={{
          flex: '0 0 auto',
          px: { xs: 3, sm: 4 },
          pt: 2,
          // When the on-screen keyboard is open on Capacitor, reduce the safe-area
          // bottom padding — the keyboard takes that space, and the BottomSheet has
          // already been repositioned to sit above the keyboard.
          pb: isKeyboardOpen ? 2 : 'calc(var(--safe-area-inset-bottom, 0px) + 16px)',
        }}>
          <CommentComposer
            parent={commentParentFor(post)}
            currentUserId={user.uid}
            currentUserName={composerName}
            currentUserAvatar={composerAvatar}
            onInputFocus={scrollIntoViewOnFocus}
            onCommentAdded={() => setCommentsRefreshKey(k => k + 1)}
          />
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        maxHeight={isKeyboardOpen ? `calc(90vh - ${keyboardHeight}px)` : '90vh'}
        bottom={isKeyboardOpen ? `${keyboardHeight}px` : 0}
        zIndex={zIndex}
        disableWebHistoryBack={disableWebHistoryBack}
      >
        {content}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '85vh', ...glassStyles.dialog(isDark), borderRadius: '20px' } }}
      style={zIndex !== undefined ? { zIndex } : undefined}
    >
      {content}
    </Dialog>
  );
};

export default SocialPostDialog;
